// Version: 4.2
// v4.2: The score at the top is the IT Health Grade from the monthly IT and Cyber
//       Security Report (GET /api/clients/<slug>/it-health, app.py v4.48), which
//       looks at the inside (computers, patching, network devices, backup,
//       Microsoft 365, compliance) and the outside. It was the External Cyber
//       Security Assessment's grade, which the External assessment card already
//       shows. Change is against the previous month, the next grade uses the IT
//       report's bands (A 90, B 75, C 60, D 45, E 30) and the list is what lowered
//       the score, largest first, each with Ask us to fix this. A client with no
//       IT report yet still sees the assessment grade, labelled as such.
// v4.1: The Microsoft 365 backup card says what was backed up on the last run: items
//       backed up out of the total (44 / 45), each type with its count (Email 21/22,
//       OneDrive 21/22, SharePoint 18, Teams 3), when the last backup ran and how it
//       ended, and the first thing that went wrong ("1 mailbox was not backed up on the
//       last run"). It showed 0 / 1 tasks healthy for one task that backed up everything
//       but one mailbox and one OneDrive. Status reads Review (amber, a run that partly
//       completed) or Failed (red).
// v4.0: Light theme refresh to the shared UI rules (index.css 1.5): white cards with
//       visible edges, the 12px card corner, the shared button classes, read-only
//       status tags (the only pill shape), readable ink colours for text and sentence
//       case labels. The page no longer forces a dark background.
//       Header greets the signed-in user, names the client and has Raise a ticket and
//       View reports. The score card lists what would lift the grade, each with Ask us
//       to fix this, which raises a ticket.
//       Each card opens its hub (the whole card is clickable) and links only to hubs
//       the client has. Backup has no client page, so its card carries the detail
//       (tasks, storage, Updated) and no link. Phishing opens Compliance Hub on its tab.
//       Fixes: Microsoft 365 Secure Score never showed (it is on the result, not the
//       summary); SaaS discovery always said 0 categories (counted from the services
//       now); dates show the time and GMT; duplicate requests removed (history, the
//       log summary, Action1, compliance and tickets were each fetched two or three
//       times); widgets no longer jump in as features arrive; drag and drop removed
//       (the order could not be saved by a client, so it was lost on reload).
//       Rows are always full: a card next to a gap widens to fill it.
// v3.9: Email security widget reads the DNS check's line (SPF, DMARC, DKIM).
// v3.8: Align hub icons with stg.limited/clearview website.
// v3.7: Add hub field to WIDGET_CATALOGUE; fetch client features;
//       suppress widgets whose hub is disabled.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { authFetch, useAuth } from './auth';
import Button from './Button';
import './ClientDashboard.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/London',
  }) + ' GMT';
}

// "3 issue(s) found across 2 domain(s)." -> "3 issues found across 2 domains"
function tidy(text) {
  if (!text) return '';
  return text
    .replace(/(\d+) ([A-Za-z]+)\(s\)/g, (m, n, w) => `${n} ${w}${n === '1' ? '' : 's'}`)
    .replace(/\.\s*$/, '');
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function gradeTone(letter) {
  if (!letter) return 'muted';
  if (letter === 'A' || letter === 'B') return 'green';
  if (letter === 'C' || letter === 'D') return 'amber';
  return 'red';
}

// Same bands as the report generator: A 90, B 78, C 62, D 46
function scoreTone(score) {
  if (score == null) return 'muted';
  if (score >= 78) return 'green';
  if (score >= 46) return 'amber';
  return 'red';
}

function pctTone(pct, greenAt = 80, amberAt = 60) {
  if (pct == null) return 'muted';
  if (pct >= greenAt) return 'green';
  if (pct >= amberAt) return 'amber';
  return 'red';
}

function ragFromText(text) {
  if (!text) return 'muted';
  if (/(^|[^0-9])0 (issue|finding|scored listing|listing)|no issue/i.test(text)) return 'green';
  if (/critical|high/i.test(text)) return 'red';
  return 'amber';
}

const STATUS_WORD = { green: 'Good', amber: 'Review', red: 'At risk', muted: 'No data' };
const TAG_CLASS   = { green: 'tag-green', amber: 'tag-amber', red: 'tag-red', accent: 'tag-blue', muted: 'tag-muted' };

// One request per address per dashboard visit, shared by every card
function useLoader(slug) {
  return useMemo(() => {
    const cache = {};
    return (path) => {
      if (!cache[path]) {
        cache[path] = authFetch(path)
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null);
      }
      return cache[path];
    };
  }, [slug]);
}

function useData(load, path) {
  const [state, setState] = useState({ loading: !!path, data: null });
  useEffect(() => {
    let live = true;
    if (!path) { setState({ loading: false, data: null }); return undefined; }
    setState(s => ({ ...s, loading: true }));
    load(path).then(d => { if (live) setState({ loading: false, data: d }); });
    return () => { live = false; };
  }, [load, path]);
  return state;
}

// Newest assessment and its log summary (score, change, what each check found)
function useAssessment(load, slug, enabled) {
  const hist = useData(load, enabled ? `/api/clients/${slug}/history` : null);
  const history = Array.isArray(hist.data) ? hist.data : [];
  const latest = history[0];
  const run = latest ? (latest.location === 'archive' ? latest.archive_run : (latest.log_run || 'current')) : null;
  const sum = useData(load, run ? `/api/clients/${slug}/history/${run}/log-summary` : null);
  return {
    loading: hist.loading || sum.loading,
    history,
    latest,
    summary: sum.data && sum.data.available !== false ? sum.data : null,
  };
}

// ── Small pieces ─────────────────────────────────────────────────────────────

function Tag({ tone = 'muted', children }) {
  return <span className={`tag ${TAG_CLASS[tone] || 'tag-muted'}`}>{children}</span>;
}

function Bar({ pct, tone }) {
  const w = Math.max(0, Math.min(100, Math.round(pct || 0)));
  return (
    <div className="cv-bar" role="presentation">
      <div className={`cv-bar-fill fill-${tone}`} style={{ width: `${w}%` }} />
    </div>
  );
}

function Ring({ pct, tone, size = 64, stroke = 6, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="cv-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" className="cv-ring-track" strokeWidth={stroke} />
        {pct != null && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" className={`cv-ring-arc stroke-${tone}`}
            strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={c * (1 - p / 100)}
            strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        )}
      </svg>
      <div className="cv-ring-inner">{children}</div>
    </div>
  );
}

function Mini({ value, label, tone }) {
  return (
    <div className="cv-mini">
      <div className={`cv-mini-val tone-${tone || 'text'}`}>{value}</div>
      <div className="cv-mini-lbl">{label}</div>
    </div>
  );
}

// ── Card shell ───────────────────────────────────────────────────────────────

function Card({ icon, tone = 'accent', title, status, statusTone, linkTo, linkLabel = 'View',
                wide, loading, empty, footer, children }) {
  const navigate = useNavigate();
  const open = (e) => {
    if (!linkTo || e.target.closest('a, button')) return;
    navigate(linkTo);
  };
  return (
    <div className={`cv-card${wide ? ' cv-wide' : ''}${linkTo ? ' cv-clickable' : ''}`} onClick={open}>
      <div className="cv-card-head">
        <div className={`cv-icon icon-${tone}`}><i className={`ti ${icon}`} aria-hidden="true" /></div>
        <div className="cv-card-title">{title}</div>
        {!loading && !empty && status && <Tag tone={statusTone}>{status}</Tag>}
      </div>

      {loading ? (
        <div className="cv-card-body">
          <div className="cv-skel cv-skel-lg" />
          <div className="cv-skel" />
        </div>
      ) : empty ? (
        <div className="cv-card-empty">{empty}</div>
      ) : (
        <div className="cv-card-body">{children}</div>
      )}

      {(footer || linkTo) && !loading && (
        <div className="cv-card-foot">
          <div className="cv-card-note">{!empty && footer}</div>
          {linkTo && (
            <Link to={linkTo} className="btn btn-quiet btn-sm cv-card-link">
              {linkLabel} <i className="ti ti-arrow-right" aria-hidden="true" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ── Raise a ticket ───────────────────────────────────────────────────────────

function RaiseTicketModal({ slug, preset, onClose }) {
  const [type, setType]         = useState('Request');
  const [priority, setPriority] = useState('Medium');
  const [title, setTitle]       = useState(preset?.title || '');
  const [desc, setDesc]         = useState(preset?.description || '');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    if (!title.trim()) { setError('Add a short title so we know what this is about.'); return; }
    setBusy(true); setError('');
    try {
      const res = await authFetch('/api/tickets', {
        method: 'POST',
        body: JSON.stringify({ type, priority, title: title.trim(), description: desc.trim(),
                               client_slug: slug, source: 'portal' }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'The ticket could not be raised. Please try again.');
      setDone(d.ref || d.ticket?.ref || d.id || true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box cv-modal" role="dialog" aria-modal="true" aria-labelledby="cv-ticket-title"
           onClick={e => e.stopPropagation()}>
        <div className="cv-modal-head">
          <div className="modal-title" id="cv-ticket-title">Raise a ticket</div>
          <Button variant="quiet" icon size="sm" title="Close" onClick={onClose}>×</Button>
        </div>
        {done ? (
          <div className="cv-modal-done">
            <i className="ti ti-circle-check" aria-hidden="true" />
            <div className="cv-modal-done-title">Ticket raised{typeof done === 'string' ? ` (${done})` : ''}</div>
            <div className="cv-modal-done-sub">We have it and will be in touch. You can follow it in Tickets.</div>
            <div className="cv-modal-actions">
              <Link to={`/clients/${slug}/tickets`} className="btn btn-secondary">Open tickets</Link>
              <Button variant="primary" onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="cv-modal-form">
            <div className="cv-modal-row">
              <div>
                <label htmlFor="cv-t-type">What do you need?</label>
                <select id="cv-t-type" value={type} onChange={e => setType(e.target.value)}>
                  <option value="Request">Help or a change</option>
                  <option value="Incident">Something is not working</option>
                </select>
              </div>
              <div>
                <label htmlFor="cv-t-prio">Priority</label>
                <select id="cv-t-prio" value={priority} onChange={e => setPriority(e.target.value)}>
                  {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="cv-t-title">Title</label>
              <input id="cv-t-title" value={title} onChange={e => setTitle(e.target.value)}
                     placeholder="A few words about the problem or request" autoFocus />
            </div>
            <div>
              <label htmlFor="cv-t-desc">Details</label>
              <textarea id="cv-t-desc" rows={5} value={desc} onChange={e => setDesc(e.target.value)}
                        placeholder="Anything that helps us pick this up quickly" />
            </div>
            {error && <div className="error-msg">{error}</div>}
            <div className="cv-modal-actions">
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={submit} disabled={busy}>
                {busy ? <span className="spinner" /> : 'Raise ticket'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Score card (top of the page) ─────────────────────────────────────────────

// IT report bands (generate-report-clearview.py): A 90, B 75, C 60, D 45
function itTone(score) {
  if (score == null) return 'muted';
  if (score >= 75) return 'green';
  if (score >= 45) return 'amber';
  return 'red';
}

function changeText(delta, since) {
  if (delta == null) return null;
  if (delta > 0) return `Up ${plural(delta, 'point')} since ${since}.`;
  if (delta < 0) return `Down ${plural(Math.abs(delta), 'point')} since ${since}.`;
  return `No change since ${since}.`;
}

function ScoreCard({ slug, load, assessment, can, onRaise }) {
  const a1      = useData(load, can('hub_device') ? `/api/clients/${slug}/action1` : null);
  const tickets = useData(load, `/api/tickets/stats?client_slug=${encodeURIComponent(slug)}`);
  const health  = useData(load, `/api/clients/${slug}/it-health`);
  const { latest, summary, loading: aLoading } = assessment;
  const it      = health.data?.available ? health.data : null;
  const loading = health.loading || (!it && aLoading);
  // The score shows when there is an IT report, the Cyber Security Hub or an assessment
  const cyber   = !!it || can('hub_cyber') || !!latest;

  // IT Health Grade first; the assessment grade only until the first IT report
  const grade   = it ? it.grade : (summary?.grade || latest?.grade || null);
  const letter  = grade ? grade[0] : null;
  const score   = it ? it.score : (summary?.score ?? latest?.score ?? null);
  const tone    = letter ? gradeTone(letter) : (it ? itTone(score) : scoreTone(score));
  const word    = tone === 'green' ? 'Good' : tone === 'amber' ? 'Needs attention' : tone === 'red' ? 'At risk' : null;
  const kicker  = it || !latest ? 'IT and cyber health score' : 'External security score';
  const next    = it ? it.next_grade : summary?.next_grade;
  const need    = it ? it.points_needed : summary?.points_needed;
  const actions = it ? (it.lifts || []) : (summary?.next_grade_actions || []);
  const updated = it ? it.generated_at : latest?.date;
  const devices = a1.data?.data?.summary?.total;
  const open    = tickets.data?.open;

  if (!cyber && !can('hub_device')) return null;   // tickets alone: the Support tickets card has them

  const desc = loading ? 'Loading your latest results…'
    : it ? (changeText(it.score_delta, 'last month') || 'From your monthly IT and Cyber Security Report.')
    : !latest ? 'Your score appears here after your first monthly IT and Cyber Security Report.'
    : changeText(summary?.score_delta, 'the previous assessment') || 'From your latest External Cyber Security Assessment.';

  const nextTitle = !next ? null
    : it?.capped ? `Fix this first to get past grade ${grade}`
    : need ? `${plural(need, 'point')} to grade ${next}`
    : `Towards grade ${next}`;

  return (
    <div className={`cv-hero${cyber ? '' : ' cv-hero-plain'}`}>
      {cyber && (
        <div className="cv-hero-score">
          <Ring pct={score} tone={tone} size={104} stroke={8}>
            <div className={`cv-hero-num tone-${tone}`}>{loading || score == null ? '–' : score}</div>
            <div className="cv-hero-of">of 100</div>
          </Ring>
          <div className="cv-hero-body">
            <div className="cv-hero-kicker">{kicker}</div>
            <div className="cv-hero-title">
              {grade ? `Grade ${grade}` : 'Not graded yet'}
              {word && <Tag tone={tone}>{word}</Tag>}
            </div>
            <div className="cv-hero-desc">{desc}</div>
            {updated && <div className="cv-hero-updated">Updated {fmt(updated)}</div>}
          </div>
        </div>
      )}

      <div className="cv-hero-stats">
        {can('hub_device') && (
          <div className="cv-hero-stat">
            <div className="cv-hero-stat-val">{devices ?? '—'}</div>
            <div className="cv-hero-stat-lbl">Managed devices</div>
          </div>
        )}
        <div className="cv-hero-stat">
          <div className="cv-hero-stat-val">{open ?? '—'}</div>
          <div className="cv-hero-stat-lbl">Open tickets</div>
        </div>
      </div>

      {cyber && actions.length > 0 && nextTitle && (
        <div className="cv-hero-next">
          <div className="cv-hero-next-title">{nextTitle}</div>
          <ul className="cv-hero-next-list">
            {actions.slice(0, 3).map(act => (
              <li key={act.key || act.tool || act.label}>
                <span>{act.label}</span>
                <Button size="sm" variant="quiet"
                  onClick={() => onRaise({
                    title: act.label,
                    description: `From our dashboard (grade ${grade}, score ${score}): please help us with "${act.label}" so we can reach grade ${next}.`,
                  })}>
                  Ask us to fix this
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Cards ────────────────────────────────────────────────────────────────────

function CyberGradeCard({ slug, assessment, link }) {
  const { latest, summary, loading } = assessment;
  const grade = summary?.grade || latest?.grade;
  const score = summary?.score ?? latest?.score;
  const delta = summary?.score_delta;
  const tone  = gradeTone(grade ? grade[0] : null);
  return (
    <Card icon="ti-shield-check" tone={latest ? tone : 'accent'} title="External assessment"
      status={delta ? (delta > 0 ? `Up ${delta}` : `Down ${Math.abs(delta)}`) : STATUS_WORD[tone]}
      statusTone={delta ? (delta > 0 ? 'green' : 'red') : tone}
      loading={loading} linkTo={link(`/clients/${slug}/reports`, null)} linkLabel="Reports"
      empty={!latest && 'No assessment yet'}
      footer={latest && `Updated ${fmt(latest.date)}`}>
      <div className="cv-value-row">
        <div className={`cv-value tone-${tone}`}>{grade}</div>
        {score != null && <div className="cv-value-unit">{score} / 100</div>}
      </div>
      <Bar pct={score} tone={tone} />
    </Card>
  );
}

const FRAMEWORKS = [
  { match: k => k.includes('cyber essentials'), short: 'Cyber Essentials' },
  { match: k => k.includes('gdpr') || k.includes('general data'), short: 'GDPR' },
  { match: k => k.includes('iso'), short: 'ISO 27001' },
];

function ComplianceCard({ slug, load, link }) {
  const { loading, data } = useData(load, `/api/clients/${slug}/compliance`);
  const posture = data?.data?.posture || {};
  const rows = Object.entries(posture).slice(0, 3).map(([k, v]) => {
    const fw  = FRAMEWORKS.find(f => f.match(k.toLowerCase()));
    const pct = v?.not_configured ? null : v?.requirements?.percent_compliant;
    return { key: k, label: fw ? fw.short : k, pct: pct == null ? null : Math.round(pct) };
  });
  return (
    <Card icon="ti-clipboard-check" tone="accent" title="Compliance"
      loading={loading} linkTo={link(`/clients/${slug}/compliance`, 'hub_compliance')}
      empty={(!data?.available || rows.length === 0) && 'No compliance data yet'}
      footer={data?.pushed_at && `Updated ${fmt(data.pushed_at)}`}>
      <div className="cv-rings">
        {rows.map(r => (
          <div key={r.key} className="cv-ring-item">
            <Ring pct={r.pct} tone={pctTone(r.pct, 75, 50)} size={60} stroke={6}>
              <span className={`cv-ring-pct tone-${pctTone(r.pct, 75, 50)}`}>{r.pct == null ? '–' : `${r.pct}%`}</span>
            </Ring>
            <div className="cv-ring-lbl">{r.label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function M365Card({ slug, load, link }) {
  const { loading, data } = useData(load, `/api/clients/${slug}/m365/results`);
  const res      = data?.data?.results || data?.data || {};
  const summary  = res.summary || {};
  const secure   = res.secure_score?.pct ?? summary.secure_score?.pct ?? null;
  const assess   = summary.assessment_score ?? res.assessment_score ?? null;
  const mfaBase  = summary.mfa_base_count ?? summary.total_users ?? 0;
  const mfaOn    = summary.mfa_enabled_count;
  const mfa      = mfaBase > 0 && mfaOn != null ? Math.min(100, Math.round((mfaOn / mfaBase) * 100)) : null;
  const critical = summary.findings_critical ?? 0;
  const high     = summary.findings_high ?? 0;

  const rank = { muted: 9, green: 3, amber: 2, red: 1 };
  const parts = [pctTone(assess), pctTone(secure), pctTone(mfa, 80, 50)].filter(t => t !== 'muted');
  const tone = critical > 0 ? 'red' : parts.length ? parts.reduce((w, t) => (rank[t] < rank[w] ? t : w)) : 'muted';
  const main = assess ?? secure;

  return (
    <Card icon="ti-layout-grid" tone="purple" title="Microsoft 365"
      status={STATUS_WORD[tone]} statusTone={tone}
      loading={loading} linkTo={link(`/clients/${slug}/productivity`, 'hub_productivity')}
      empty={!data?.data && 'Not connected yet'}
      footer={critical > 0 ? `${plural(critical, 'critical finding')} to fix`
        : high > 0 ? `${plural(high, 'high finding')} to review`
        : summary.enabled_users != null ? `${plural(summary.enabled_users, 'active user')}` : null}>
      <div className="cv-minis">
        {assess != null && <Mini value={Math.round(assess)} label="Security score" tone={pctTone(assess)} />}
        {secure != null && <Mini value={`${Math.round(secure)}%`} label="Secure Score" tone={pctTone(secure)} />}
        {mfa != null && <Mini value={`${mfa}%`} label="MFA on" tone={pctTone(mfa, 80, 50)} />}
      </div>
      {main != null && <Bar pct={main} tone={pctTone(main)} />}
    </Card>
  );
}

function DevicesCard({ slug, load, link }) {
  const a1  = useData(load, `/api/clients/${slug}/action1`);
  const net = useData(load, `/api/clients/${slug}/network`);
  const s         = a1.data?.data?.summary || {};
  const total     = s.total ?? 0;
  const compliant = s.compliant ?? 0;
  const critical  = s.with_critical ?? 0;
  const hosts     = net.data?.summary?.total_hosts ?? 0;
  const managed   = !!a1.data?.available;
  const tone      = !managed ? 'muted' : critical > 0 ? 'red' : compliant < total ? 'amber' : 'green';
  return (
    <Card icon="ti-device-laptop" tone="accent" title="Device security"
      status={managed ? STATUS_WORD[tone] : null} statusTone={tone}
      loading={a1.loading || net.loading} linkTo={link(`/clients/${slug}/devices`, 'hub_device')}
      empty={!managed && !net.data?.available && 'No device data yet'}
      footer={managed
        ? (critical > 0 ? `${plural(critical, 'device')} missing critical updates` : 'No critical updates outstanding')
        : `${plural(hosts, 'device')} seen on your network`}>
      <div className="cv-value-row">
        <div className="cv-value">{managed ? total : hosts}</div>
        <div className="cv-value-unit">
          {managed ? `${compliant} of ${total} up to date` : 'on your network'}
        </div>
      </div>
      {managed && <Bar pct={total ? (compliant / total) * 100 : 0} tone={tone} />}
      {managed && hosts > 0 && <div className="cv-subline">{plural(hosts, 'device')} seen on your network</div>}
    </Card>
  );
}

// v4.1: what was backed up on the last run, per type, not just how many tasks were healthy
const BACKUP_TYPES = [
  ['Exchange Online', 'Email'], ['OneDrive for Business', 'OneDrive'],
  ['SharePoint Online', 'SharePoint'], ['Microsoft Teams', 'Teams'],
];
const RUN_WORDS = { completed: 'completed', partial: 'partially completed', cancelled: 'cancelled',
                    failed: 'failed', other: 'unknown result' };

function BackupCard({ slug, load }) {
  const { loading, data } = useData(load, `/api/clients/${slug}/backup`);
  const d       = data?.data || {};
  const s       = d.summary || {};
  const tasks   = Array.isArray(d.tasks) ? d.tasks : [];
  const store   = d.nas_storage;
  const health  = t => String(t.health || t.overall_status || '').toLowerCase();
  const nTasks  = tasks.length || (s.total_tasks ?? 0);
  const crit    = tasks.length ? tasks.filter(t => ['critical', 'failed'].includes(health(t))).length : (s.failed ?? 0);
  const okTasks = tasks.length ? tasks.filter(t => health(t) === 'healthy').length : (s.successful ?? 0);
  const tone    = !data?.available || nTasks === 0 ? 'muted' : crit > 0 ? 'red' : okTasks === nTasks ? 'green' : 'amber';
  const word    = { green: 'Healthy', amber: 'Review', red: 'Failed', muted: 'No data' }[tone];

  // Items per type across tasks (edge backup-check v1.6 service_counts)
  const counts = {};
  tasks.forEach(t => Object.entries(t.service_counts || {}).forEach(([k, c]) => {
    const a = counts[k] || (counts[k] = { healthy: 0, total: 0 });
    a.healthy += c.healthy || 0; a.total += c.total || 0;
  }));
  if (!tasks.length) Object.assign(counts, s.service_counts || {});
  const types   = BACKUP_TYPES.filter(([k]) => (counts[k]?.total || 0) > 0);
  const itemsOk = types.reduce((n, [k]) => n + counts[k].healthy, 0);
  const items   = types.reduce((n, [k]) => n + counts[k].total, 0);

  // The newest run across tasks, and what went wrong on it
  const last    = tasks.filter(t => t.last_run_at).sort((a, b) => (a.last_run_at < b.last_run_at ? 1 : -1))[0];
  const risks   = [...new Set(tasks.flatMap(t => (health(t) === 'healthy' ? [] : (t.risks || []))).filter(Boolean))];
  const risk    = risks.slice(0, 2).join('; ') + (risks.length > 2 ? `; and ${risks.length - 2} more` : '');
  const lastTxt = last ? `Last backup ${fmt(last.last_run_at)}${RUN_WORDS[last.last_result] ? `: ${RUN_WORDS[last.last_result]}` : ''}` : '';

  return (
    <Card icon="ti-database" tone="accent" title="Microsoft 365 backup"
      status={word} statusTone={tone} loading={loading}
      empty={!data?.available && 'No backup data yet'}
      footer={data?.pushed_at && `Updated ${fmt(data.pushed_at)}`}>
      {items > 0 ? (
        <>
          <div className="cv-value-row">
            <div className={`cv-value tone-${tone}`}>{itemsOk}<span className="cv-value-of"> / {items}</span></div>
            <div className="cv-value-unit">backed up on the last run</div>
          </div>
          <Bar pct={(itemsOk / items) * 100} tone={tone} />
          <div className="cv-minis">
            {types.map(([k, label]) => {
              const c = counts[k];
              return <Mini key={k} value={c.healthy === c.total ? c.total : `${c.healthy}/${c.total}`} label={label}
                tone={c.healthy === c.total ? 'green' : c.healthy === 0 ? 'red' : 'amber'} />;
            })}
          </div>
        </>
      ) : (
        <>
          <div className="cv-value-row">
            <div className={`cv-value tone-${tone}`}>{okTasks}<span className="cv-value-of"> / {nTasks}</span></div>
            <div className="cv-value-unit">backup {nTasks === 1 ? 'task' : 'tasks'} healthy</div>
          </div>
          <Bar pct={nTasks ? (okTasks / nTasks) * 100 : 0} tone={tone} />
        </>
      )}
      <div className="cv-subline">
        {lastTxt}{lastTxt && (risk || tone === 'green') ? '. ' : ''}
        {risk ? `${risk.charAt(0).toUpperCase()}${risk.slice(1)}.` : tone === 'green' ? 'Everything was backed up.' : ''}
        {store?.used && store?.total ? ` ${store.used} of ${store.total} used.` : ''}
      </div>
    </Card>
  );
}

function TicketsCard({ slug, load, onRaise }) {
  const { loading, data } = useData(load, `/api/tickets/stats?client_slug=${encodeURIComponent(slug)}`);
  const open     = data?.open ?? 0;
  const prog     = data?.in_progress ?? 0;
  const critical = data?.critical_open ?? 0;
  const total    = data?.total ?? 0;
  const tone     = critical > 0 ? 'red' : open > 0 ? 'amber' : 'green';
  return (
    <Card icon="ti-ticket" tone="accent" title="Support tickets"
      status={critical > 0 ? `${critical} critical` : open > 0 ? `${open} open` : 'All clear'} statusTone={tone}
      loading={loading} linkTo={`/clients/${slug}/tickets`}
      empty={!data && 'Tickets are not available right now'}
      footer={<Button size="sm" onClick={() => onRaise(null)}>Raise a ticket</Button>}>
      <div className="cv-minis">
        <Mini value={open} label="Open" tone={open > 0 ? 'amber' : 'green'} />
        <Mini value={prog} label="In progress" />
        <Mini value={total} label="All time" tone="muted" />
      </div>
    </Card>
  );
}

function EmailCard({ slug, assessment, link }) {
  const { loading, summary } = assessment;
  const line = (summary?.summaries || []).find(s => s.tool === 'email_security')
            || (summary?.summaries || []).find(s => s.tool === 'DNS');
  const text = tidy(line?.text);
  const tone = !summary ? 'muted' : line ? ragFromText(line.text) : 'muted';
  const word = { green: 'Secure', amber: 'Review', red: 'At risk', muted: 'No data' }[tone];
  const m    = text.match(/^(\d+) issues? (found )?(.*)$/i);
  return (
    <Card icon="ti-mail" tone="accent" title="Email security" status={word} statusTone={tone}
      loading={loading} linkTo={link(`/clients/${slug}/cyber-security`, 'hub_cyber')}
      empty={!summary && 'No assessment yet'}
      footer="SPF, DKIM and DMARC records">
      {m ? (
        <div className="cv-value-row">
          <div className={`cv-value tone-${tone}`}>{m[1]}</div>
          <div className="cv-value-unit">{m[1] === '1' ? 'issue' : 'issues'} {m[3]}</div>
        </div>
      ) : (
        <div className="cv-subline">{text || 'No result in the latest assessment'}</div>
      )}
    </Card>
  );
}

function AlertsCard({ slug, load, link }) {
  const { loading, data } = useData(load, '/api/resources/cyber-alerts');
  const alerts = (data?.alerts || []).slice(0, 3);
  return (
    <Card icon="ti-alert-triangle" tone="amber" title="UK cyber alerts"
      loading={loading} linkTo={link(`/clients/${slug}/resources`, 'hub_resources')} linkLabel="All alerts"
      empty={alerts.length === 0 && 'No alerts to show right now'}>
      <ul className="cv-list">
        {alerts.map((a, i) => (
          <li key={a.url || i}>
            <a href={a.url} target="_blank" rel="noreferrer" className="cv-list-link">
              <span className="cv-list-title">{a.title}</span>
              {a.date && <span className="cv-list-meta">{a.date}</span>}
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function WebCard({ slug, load, link }) {
  const seo    = useData(load, `/api/clients/${slug}/seo`);
  const expiry = useData(load, `/api/clients/${slug}/domain-expiry`);
  const certs  = useData(load, `/api/clients/${slug}/certs`);
  const sum    = seo.data?.summary || {};
  const domains = seo.data?.domains?.length || expiry.data?.domains?.length || 0;

  const perf     = typeof sum.perf_desktop_avg === 'number' ? sum.perf_desktop_avg
                 : typeof sum.perf_mobile_avg === 'number' ? sum.perf_mobile_avg : null;
  const mobile   = typeof sum.perf_mobile_avg === 'number' ? sum.perf_mobile_avg : null;
  const cs       = certs.data?.data?.summary || {};
  const checked  = cs.total_checked ?? Object.keys(certs.data?.data?.expiry_checks || {}).length;
  const expired  = cs.expired ?? 0;
  const soon     = cs.expiring_soon ?? 0;
  const sslTone  = !certs.data?.available || !checked ? 'muted' : expired ? 'red' : soon ? 'amber' : 'green';
  const expiring = (expiry.data?.domains || []).filter(d => {
    const days = d.days_remaining ?? (d.expiry_date ? (new Date(d.expiry_date) - new Date()) / 86400000 : null);
    return days != null && days > 0 && days <= 60;
  }).length;

  const loading = seo.loading || expiry.loading || certs.loading;
  const hasData = seo.data?.available || domains > 0;

  return (
    <Card wide icon="ti-world" tone="accent" title="Website, SEO and Geo"
      loading={loading} linkTo={link(`/clients/${slug}/domains`, 'hub_digital')}
      empty={!hasData && 'No website data yet'}
      footer={seo.data?.scanned_at && `Updated ${fmt(seo.data.scanned_at)}`}>
      <div className="cv-tiles">
        <div className="cv-tile">
          <div className={`cv-tile-val tone-${pctTone(perf)}`}>{perf != null ? `${perf}%` : '—'}</div>
          <div className="cv-tile-lbl">Page speed</div>
          <div className="cv-tile-sub">{mobile != null && perf !== mobile ? `Desktop · mobile ${mobile}%` : 'Desktop average'}</div>
        </div>
        <div className="cv-tile">
          <div className={`cv-tile-val tone-${sslTone}`}>
            {expired ? `${expired} expired` : soon ? `${soon} expiring` : checked ? 'Valid' : '—'}
          </div>
          <div className="cv-tile-lbl">Certificates</div>
          <div className="cv-tile-sub">{checked ? `${plural(checked, 'certificate')} checked` : 'Not checked yet'}</div>
        </div>
        <div className="cv-tile">
          <div className={`cv-tile-val tone-${expiring ? 'amber' : 'text'}`}>{domains || '—'}</div>
          <div className="cv-tile-lbl">Domains</div>
          <div className="cv-tile-sub">{expiring ? `${expiring} renewing within 60 days` : 'All registrations current'}</div>
        </div>
      </div>
      <div className="cv-tags">
        {sum.seo_score_avg != null && (
          <Tag tone={pctTone(sum.seo_score_avg, 70, 50)}>SEO {sum.seo_score_avg}%{sum.total_seo_issues ? ` · ${plural(sum.total_seo_issues, 'issue')}` : ''}</Tag>
        )}
        {sum.geo_score_avg != null && (
          <Tag tone={pctTone(sum.geo_score_avg, 70, 50)}>Geo {sum.geo_score_avg}%{sum.total_geo_issues ? ` · ${plural(sum.total_geo_issues, 'issue')}` : ''}</Tag>
        )}
        {sum.total_recommendations > 0 && <Tag tone="muted">{plural(sum.total_recommendations, 'recommendation')}</Tag>}
      </div>
    </Card>
  );
}

function HistoryCard({ slug, assessment, link }) {
  const { loading, history } = assessment;
  const rows = history.slice(0, 4);
  return (
    <Card wide icon="ti-history" tone="accent" title="Assessment history"
      loading={loading} linkTo={link(`/clients/${slug}/reports`, null)} linkLabel="All reports"
      empty={rows.length === 0 && 'No assessments yet'}>
      <ul className="cv-runs">
        {rows.map((h, i) => {
          const prev  = history[i + 1];
          const delta = h.score != null && prev?.score != null ? h.score - prev.score : null;
          const L     = h.grade ? h.grade[0] : null;
          return (
            <li key={h.filename || h.date} className="cv-run">
              <span className={`grade-badge grade-${L || 'unknown'} cv-run-grade`}>{h.grade || '?'}</span>
              <span className="cv-run-body">
                <span className="cv-run-title">External Cyber Security Assessment</span>
                <span className="cv-run-meta">{fmt(h.date)}</span>
              </span>
              <span className="cv-run-score">
                {h.score != null && <span className="cv-run-num">{h.score}</span>}
                {delta != null && delta !== 0 && <Tag tone={delta > 0 ? 'green' : 'red'}>{delta > 0 ? `+${delta}` : delta}</Tag>}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function SaasCard({ slug, load, link }) {
  const { loading, data } = useData(load, `/api/clients/${slug}/saas`);
  const services = (data?.data?.services || []).filter(s => s.detected !== false);
  const cats = new Set(services.map(s => s.category).filter(Boolean));
  const catCount = Object.keys(data?.data?.categories || {}).length || cats.size;
  return (
    <Card icon="ti-topology-star" tone="purple" title="SaaS discovery"
      loading={loading} linkTo={link(`/clients/${slug}/productivity`, 'hub_productivity')}
      empty={!data?.available && 'No scan yet'}
      footer={data?.data?.scanned_at && `Updated ${fmt(data.data.scanned_at)}`}>
      <div className="cv-value-row">
        <div className="cv-value">{services.length}</div>
        <div className="cv-value-unit">online {services.length === 1 ? 'service' : 'services'} found</div>
      </div>
      <div className="cv-subline">{catCount ? `Across ${plural(catCount, 'category')}` : 'No categories yet'}</div>
    </Card>
  );
}

function PhishingCard({ slug, load, link }) {
  const { loading, data } = useData(load, `/api/clients/${slug}/phishing/summary`);
  const rag   = data?.rag === 'grey' || !data?.rag ? 'muted' : data.rag;
  const rate  = data?.latest_click_rate;
  const word  = { green: 'Low risk', amber: 'Moderate', red: 'High risk', muted: 'No data' }[rag];
  return (
    <Card icon="ti-fish-hook" tone="amber" title="Phishing simulation" status={word} statusTone={rag}
      loading={loading} linkTo={link(`/clients/${slug}/compliance?tab=phishing`, 'hub_compliance')}
      empty={!data?.configured && 'No campaigns run yet'}
      footer={data?.latest_launch_date && `Last run ${fmt(data.latest_launch_date)}`}>
      <div className="cv-minis">
        {rate != null && <Mini value={`${rate}%`} label="Clicked the link" tone={rag} />}
        {data?.total_campaigns != null && <Mini value={data.total_campaigns} label="Campaigns" />}
      </div>
      {data?.latest_campaign && <div className="cv-subline">Latest: {data.latest_campaign}</div>}
    </Card>
  );
}

// ── Widget catalogue ──────────────────────────────────────────────────────────
// Ids, sizes and hubs are shared with Client Management (WIDGET_HUB_MAP) and the
// saved dashboard_config.json, so they stay as they are.

const WIDGET_CATALOGUE = [
  { id: 'cyber_grade',    label: 'External assessment',   size: '1x1', defaultOn: true,  hub: 'hub_cyber' },
  { id: 'compliance',     label: 'Compliance',            size: '1x1', defaultOn: true,  hub: 'hub_compliance' },
  { id: 'productivity',   label: 'Microsoft 365',         size: '1x1', defaultOn: true,  hub: 'hub_productivity' },
  { id: 'devices',        label: 'Device security',       size: '1x1', defaultOn: true,  hub: 'hub_device' },
  { id: 'backup',         label: 'Microsoft 365 backup',  size: '1x1', defaultOn: true,  hub: 'hub_compliance' },
  { id: 'tickets',        label: 'Support tickets',       size: '1x1', defaultOn: true,  hub: null },
  { id: 'web_presence',   label: 'Website, SEO and Geo',  size: '2x1', defaultOn: true,  hub: 'hub_digital' },
  { id: 'email_security', label: 'Email security',        size: '1x1', defaultOn: true,  hub: 'hub_cyber' },
  { id: 'activity',       label: 'Assessment history',    size: '2x1', defaultOn: true,  hub: null },
  { id: 'phishing',       label: 'Phishing simulation',   size: '1x1', defaultOn: true,  hub: null },
  { id: 'alerts',         label: 'UK cyber alerts',       size: '1x1', defaultOn: true,  hub: null },
  { id: 'saas',           label: 'SaaS discovery',        size: '1x1', defaultOn: false, hub: 'hub_cyber' },
];

// Widths per card so every row is full: a card that would leave a gap (because
// the next one is too wide for what is left of the row) widens to fill it.
function packSpans(sizes, cols) {
  const spans = [];
  let left = cols, rowLast = -1;
  sizes.forEach((size, i) => {
    const w = Math.min(size, cols);
    if (w > left && rowLast >= 0) { spans[rowLast] += left; left = cols; }
    spans[i] = w;
    left -= w;
    rowLast = i;
    if (left === 0) { left = cols; rowLast = -1; }
  });
  if (left < cols && rowLast >= 0) spans[rowLast] += left;
  return spans;
}

const DEFAULT_WIDGETS = WIDGET_CATALOGUE.filter(w => w.defaultOn).map(w => ({ id: w.id, enabled: true, size: w.size }));

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function ClientDashboard() {
  const { slug } = useParams();
  const { user } = useAuth();
  const load     = useLoader(slug);

  const config   = useData(load, slug ? `/api/clients/${slug}/dashboard-config` : null);
  const feats    = useData(load, slug ? `/api/clients/${slug}/features` : null);
  const meta     = useData(load, slug ? `/api/clients/${slug}/meta` : null);
  const features = feats.data?.features || {};
  const ready    = !config.loading && !feats.loading;

  const can = (hub) => !hub || !!features[hub];
  // A link only when the client has the hub it opens
  const link = (to, hub) => (can(hub) ? to : null);

  const assessment = useAssessment(load, slug, ready);
  const [ticket, setTicket] = useState(undefined); // undefined = closed, null = blank, {..} = preset

  const widgets = (Array.isArray(config.data?.widgets) && config.data.widgets.length ? config.data.widgets : DEFAULT_WIDGETS)
    .filter(w => w.enabled && WIDGET_CATALOGUE.some(c => c.id === w.id))
    .filter(w => can(WIDGET_CATALOGUE.find(c => c.id === w.id).hub));

  const sizes  = widgets.map(w => (w.size === '3x1' ? 3 : w.size === '2x1' || w.size === '2x2' ? 2 : 1));
  const span3  = packSpans(sizes, 3);
  const span2  = packSpans(sizes, 2);

  const first      = (user?.name || '').trim().split(/\s+/)[0] || user?.username || '';
  const clientName = meta.data?.client_name || (slug || '').replace(/_/g, ' ');
  const props      = { slug, load, link, assessment, onRaise: setTicket };

  const render = (id) => {
    switch (id) {
      case 'cyber_grade':    return <CyberGradeCard {...props} />;
      case 'compliance':     return <ComplianceCard {...props} />;
      case 'productivity':   return <M365Card       {...props} />;
      case 'devices':        return <DevicesCard    {...props} />;
      case 'backup':         return <BackupCard     {...props} />;
      case 'tickets':        return <TicketsCard    {...props} />;
      case 'email_security': return <EmailCard      {...props} />;
      case 'alerts':         return <AlertsCard     {...props} />;
      case 'web_presence':   return <WebCard        {...props} />;
      case 'activity':       return <HistoryCard    {...props} />;
      case 'saas':           return <SaasCard       {...props} />;
      case 'phishing':       return <PhishingCard   {...props} />;
      default:               return null;
    }
  };

  return (
    <div className="cv-dashboard">
      <div className="cv-header">
        <div className="cv-header-text">
          <div className="page-title">{first ? `Welcome back, ${first}` : 'Welcome back'}</div>
          <div className="page-subtitle">{clientName}</div>
        </div>
        <div className="cv-header-actions">
          <Link to={`/clients/${slug}/reports`} className="btn btn-secondary">
            <i className="ti ti-file-text" aria-hidden="true" /> View reports
          </Link>
          <Button variant="primary" onClick={() => setTicket(null)}>
            <i className="ti ti-plus" aria-hidden="true" /> Raise a ticket
          </Button>
        </div>
      </div>

      {ready ? (
        <ScoreCard slug={slug} load={load} assessment={assessment} can={can} onRaise={setTicket} />
      ) : (
        <div className="cv-hero cv-hero-loading"><div className="cv-skel cv-skel-lg" /><div className="cv-skel" /></div>
      )}

      <div className="cv-section-hdr">
        <div className="cv-section-title">Overview</div>
      </div>

      <div className="cv-grid">
        {ready
          ? widgets.map((w, i) => <div key={w.id} className={`cv-cell cv-c3-${span3[i]} cv-c2-${span2[i]}`}>{render(w.id)}</div>)
          : [0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="cv-cell"><div className="cv-card"><div className="cv-skel cv-skel-lg" /><div className="cv-skel" /></div></div>
            ))}
      </div>

      {ticket !== undefined && (
        <RaiseTicketModal slug={slug} preset={ticket} onClose={() => setTicket(undefined)} />
      )}
    </div>
  );
}
